import { useEffect, useState } from 'react';
import { AlarmClockPlus, ChevronDown, CircleStop, Loader2 } from 'lucide-react';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@clarix/ui';
import { ConfirmDialog } from '@clarix/ui-shared';
import { CHIP_COPY } from './copy';
import { formatClock } from './format';
import { useExtendOptionsQuery } from './queries';
import { useQuickMeeting } from './QuickMeetingContext';
import type { ExtendMinutes, ExtendOptionsDTO, WireEvent } from './types';
import { liveActionErrorMessage, useLiveMeetingActions } from './useLiveMeetingActions';

const MIN = 60_000;

function roomBookedNote(data: ExtendOptionsDTO, h24: boolean): string {
  const next = data.room_next;
  return next ? CHIP_COPY.roomBookedBy(next.title, formatClock(next.start, h24)) : CHIP_COPY.roomBooked;
}

interface ExtendItemsProps {
  userId: string | null;
  slug: string | null;
  event: WireEvent;
  h24: boolean;
  onPick: (minutes: ExtendMinutes) => void;
}

/** Radix mounts this only while the menu is open, which is what makes the options read on open. */
function ExtendItems({ userId, slug, event, h24, onPick }: ExtendItemsProps) {
  const query = useExtendOptionsQuery(userId, slug, event.id);
  if (query.isPending) {
    return (
      <DropdownMenuItem disabled className="text-lg">
        <Loader2 className="animate-spin" aria-hidden />
        {CHIP_COPY.extendLoading}
      </DropdownMenuItem>
    );
  }
  if (query.isError) {
    return (
      <DropdownMenuItem
        className="text-lg"
        onSelect={(e) => {
          e.preventDefault();
          void query.refetch();
        }}
      >
        {liveActionErrorMessage(query.error, CHIP_COPY.extendLoadFailed)}
      </DropdownMenuItem>
    );
  }
  const end = Date.parse(event.end);
  const booked = roomBookedNote(query.data, h24);
  return (
    <>
      {query.data.options.map((o) => {
        const note = !o.allowed ? booked : o.busy_count > 0 ? CHIP_COPY.peopleBusy(o.busy_count) : null;
        return (
          <DropdownMenuItem
            key={o.minutes}
            disabled={!o.allowed}
            onSelect={() => onPick(o.minutes)}
            className="flex-col items-start gap-0.5 text-lg"
          >
            <span className="text-foreground font-medium tabular-nums">
              {CHIP_COPY.extendOption(o.minutes, formatClock(end + o.minutes * MIN, h24))}
            </span>
            {note && <span className="text-muted-foreground text-sm [overflow-wrap:anywhere]">{note}</span>}
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

interface LiveMeetingControlsProps {
  event: WireEvent;
  h24: boolean;
  /** A menu or dialog opened from here is up, so the card's popover must not read it as outside. */
  onLayerChange: (open: boolean) => void;
}

/** Extend and End for a live meeting's host or co-host (§11.1). The server's `can` decides; absent reads as no. */
export function LiveMeetingControls({ event, h24, onLayerChange }: LiveMeetingControlsProps) {
  const qm = useQuickMeeting();
  const userId = qm?.access.userId ?? null;
  const slug = qm?.access.slug ?? null;
  const { end, extend } = useLiveMeetingActions(userId, slug, h24);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const layerOpen = menuOpen || confirming;

  useEffect(() => {
    onLayerChange(layerOpen);
    return () => onLayerChange(false);
  }, [layerOpen, onLayerChange]);

  const canExtend = event.can?.extend === true;
  const canEnd = event.can?.end === true;
  if (!canExtend && !canEnd) return null;

  return (
    <div className="flex gap-2">
      {canExtend && (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="flex-1" disabled={extend.isPending}>
              {extend.isPending ? <Loader2 className="animate-spin" aria-hidden /> : <AlarmClockPlus aria-hidden />}
              {CHIP_COPY.extend}
              <ChevronDown className="text-muted-foreground" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <ExtendItems
              userId={userId}
              slug={slug}
              event={event}
              h24={h24}
              onPick={(minutes) => extend.mutate({ event, minutes })}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {canEnd && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          disabled={end.isPending}
          onClick={() => setConfirming(true)}
        >
          <CircleStop aria-hidden />
          {CHIP_COPY.endMeeting}
        </Button>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={CHIP_COPY.endTitle}
        description={CHIP_COPY.endBody}
        cancelLabel={CHIP_COPY.keepGoing}
        confirmLabel={CHIP_COPY.endMeeting}
        loadingLabel={CHIP_COPY.ending}
        variant="destructive"
        loading={end.isPending}
        onConfirm={() => end.mutate(event, { onSettled: () => setConfirming(false) })}
      />
    </div>
  );
}
